import { Grid, Typography } from "@mui/material";
import { Button, FormDataConsumer, SimpleForm } from "react-admin";
import CloseIcon from "@mui/icons-material/Close";
import CustomTimeOnlyInput from "server/components/Fields/CustomTimeOnlyInput";
import { validateTimeEnd, validateTimeStart } from "components/LearningActivity/CreateWeeklySchedule/utils/utils";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { studentRequests } from "api/studentApi";
import moment from "moment";
import { formatDateOnlyBg } from "server/formatting/formatDateBg";
import { useEffect, useMemo, useState } from "react";
import SaveButtonSlot from "./SaveButtonSlot";
import dataProvider from "server/dataProvider";
import ExamRoomInput from "../../components/ExamRoomInput";
import { RoomSource } from "utils/constants";

const DateRange = ({ academicSessionId }) => {
  const [sessionDateRange, setSessionDateRange] = useState(null);

  useEffect(() => {
    if (academicSessionId) {
      const fetchSessionDateRange = async sessionId => {
        try {
          const { data } = await dataProvider.customRequest(
            studentRequests.getAcademicSessionDates(sessionId),
            null,
            "GET",
          );
          setSessionDateRange(data);
        } catch (e) {
          console.error("Неуспешно зареждане на периода", e);
        }
      };
      fetchSessionDateRange(academicSessionId);
    }
  }, [academicSessionId]);

  const { minDate, maxDate } = useMemo(() => {
    if (!sessionDateRange) return { minDate: null, maxDate: null };

    return { minDate: sessionDateRange.startsOn, maxDate: sessionDateRange.extendedEndsOn || sessionDateRange.endsOn };
  }, [sessionDateRange]);

  const shouldDisableDate = date => {
    if (!sessionDateRange) return false;

    const momentDate = moment(date);
    const start = moment(sessionDateRange.startsOn);
    const end = moment(sessionDateRange.extendedEndsOn || sessionDateRange.endsOn);

    return momentDate.isBefore(start, "day") || momentDate.isAfter(end, "day");
  };

  return (
    <Grid
      item
      xs={12}
      md={4}
    >
      <CustomDateInput
        source="date"
        label="Дата"
        required={true}
        minDate={minDate}
        maxDate={maxDate}
        shouldDisableDate={shouldDisableDate}
        disabled={!sessionDateRange}
      />
      {sessionDateRange && (
        <Typography
          variant="caption"
          color="text.secondary"
        >
          (Сесията е от {formatDateOnlyBg(sessionDateRange.startsOn)} до{" "}
          {formatDateOnlyBg(sessionDateRange.extendedEndsOn || sessionDateRange.endsOn)})
        </Typography>
      )}
    </Grid>
  );
};

const SessionExamSlotEditForm = ({ slot, setIsSlotInfoEditing, academicSessionId }) => {
  const isOnline = slot?.roomSource === RoomSource.GoogleCourse;
  const roomDefaults = {
    isOnline,
    roomId: isOnline ? null : slot?.roomId,
    googleCourseRefId: isOnline ? slot?.googleCourseRefId : null,
  };

  return (
    <Grid
      item
      xs={12}
    >
      <SimpleForm
        toolbar={false}
        defaultValues={{ ...slot, ...roomDefaults }}
        sx={{ pt: 0, pl: 0 }}
      >
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
          >
            <ExamRoomInput />
          </Grid>
          <DateRange academicSessionId={academicSessionId} />
          <Grid
            item
            xs={12}
            md={2}
          >
            <FormDataConsumer>
              {({ formData }) => (
                <CustomTimeOnlyInput
                  source="startTime"
                  label="Начален час"
                  validate={() => validateTimeStart(formData?.slot)}
                />
              )}
            </FormDataConsumer>
          </Grid>
          <Grid
            item
            xs={12}
            md={2}
          >
            <FormDataConsumer>
              {({ formData }) => (
                <CustomTimeOnlyInput
                  source="endTime"
                  label="Краен час"
                  validate={() => validateTimeEnd(formData?.slot)}
                />
              )}
            </FormDataConsumer>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ display: "flex", gap: 3, mt: 2 }}
          >
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              color="error"
              label="Откажи"
              onClick={() => setIsSlotInfoEditing(false)}
            />
            <SaveButtonSlot
              slot={slot}
              setIsSlotInfoEditing={setIsSlotInfoEditing}
            />
          </Grid>
        </Grid>
      </SimpleForm>
    </Grid>
  );
};

export default SessionExamSlotEditForm;
